import { FC, HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';

import { Button, ConfigProvider, Flex, Radio, Select, Typography } from 'antd';
import cn from 'classnames';
import dayjs from 'dayjs';

import { EFilters, OPTIONS_MAX_HEIGHT_NOTIFICATIONS, useFilters } from 'features/Filters';
import { PlannerDayCell, usePlannerDates } from 'features/Planner';
import { useChannelOptions } from 'shared/hooks';
import { InputClearIcon } from 'shared/ui';
import { formatDate } from 'shared/utils';

import 'dayjs/locale/ru';

import styles from './PlannerFilters.module.scss';
dayjs.locale('ru');

interface IPlannerFiltersProps extends HTMLAttributes<HTMLDivElement> {
	isInner?: boolean;
}

export const PlannerFilters: FC<IPlannerFiltersProps> = ({ className, isInner, ...props }) => {
	const { t } = useTranslation();
	const { onFilterChange, getFilterValue } = useFilters();

	const type = getFilterValue(EFilters.PLANNER_TYPE) || 'day';

	const { year, month, day, selectedDate } = usePlannerDates();

	const currentDayNumber = selectedDate.getDay() || 7;

	const onWeekChange = (type: 'prev' | 'next') => {
		const newDate = new Date(selectedDate);
		newDate.setDate(newDate.getDate() + (type === 'prev' ? -7 : 7));
		onFilterChange(EFilters.PLANNER_DATE, formatDate(newDate, 'iso'));
	};

	const startWeekDate = new Date(selectedDate);
	startWeekDate.setDate(startWeekDate.getDate() - currentDayNumber + 1);
	const endWeekDate = new Date(selectedDate);
	endWeekDate.setDate(endWeekDate.getDate() - currentDayNumber + 7);

	const onNextMonth = () => {
		const newDate = new Date(selectedDate);
		newDate.setMonth(newDate.getMonth() + 1);
		onFilterChange(EFilters.PLANNER_DATE, formatDate(newDate, 'iso'));
	};

	const onPrevMonth = () => {
		const newDate = new Date(selectedDate);
		newDate.setMonth(newDate.getMonth() - 1);
		onFilterChange(EFilters.PLANNER_DATE, formatDate(newDate, 'iso'));
	};

	const { channelOptions, isFetching } = useChannelOptions();

	return (
		<Flex {...props} vertical gap={8} className={cn(styles.filters, className)}>
			{!isInner && (
				<Select
					value={getFilterValue(EFilters.CHANNEL_IDS) || t('Все каналы и группы')}
					options={channelOptions}
					onChange={(value) => onFilterChange(EFilters.CHANNEL_IDS, value)}
					placeholder={t('Выберите каналы')}
					size="large"
					showSearch
					optionFilterProp="search"
					allowClear={getFilterValue(EFilters.CHANNEL_IDS) ? { clearIcon: <InputClearIcon /> } : false}
					listHeight={OPTIONS_MAX_HEIGHT_NOTIFICATIONS}
					style={{ width: '100%' }}
					loading={isFetching}
					disabled={isFetching}
				/>
			)}

			<Radio.Group
				value={type}
				onChange={(e) => onFilterChange(EFilters.PLANNER_TYPE, e.target.value)}
				block
				size="large">
				<Radio.Button value="day">{t('День')}</Radio.Button>
				<Radio.Button value="week">{t('Неделя')}</Radio.Button>
				<Radio.Button value="month">{t('Месяц')}</Radio.Button>
				<Radio.Button value="year">{t('Год')}</Radio.Button>
			</Radio.Group>

			<Flex gap={8}>
				<Flex className={styles.monthWrapper}>
					<Button size="large" icon={<LeftOutlined />} className={styles.monthButtonLeft} onClick={onPrevMonth} />
					<ConfigProvider theme={{ components: { Select: { borderRadiusLG: 0 } } }}>
						<Select
							value={month}
							options={[
								{ label: t('Январь'), value: '01' },
								{ label: t('Февраль'), value: '02' },
								{ label: t('Март'), value: '03' },
								{ label: t('Апрель'), value: '04' },
								{ label: t('Май'), value: '05' },
								{ label: t('Июнь'), value: '06' },
								{ label: t('Июль'), value: '07' },
								{ label: t('Август'), value: '08' },
								{ label: t('Сентябрь'), value: '09' },
								{ label: t('Октябрь'), value: '10' },
								{ label: t('Ноябрь'), value: '11' },
								{ label: t('Декабрь'), value: '12' },
							]}
							onChange={(value) => onFilterChange(EFilters.PLANNER_DATE, `${year}-${value}-${day}`)}
							placeholder={t('Выберите месяц')}
							size="large"
							showSearch
							optionFilterProp="label"
							listHeight={OPTIONS_MAX_HEIGHT_NOTIFICATIONS}
							style={{ width: '100%' }}
						/>
					</ConfigProvider>
					<Button size="large" icon={<RightOutlined />} className={styles.monthButtonRight} onClick={onNextMonth} />
				</Flex>

				<Select
					value={year}
					options={[
						{ label: '2025', value: '2025' },
						{ label: '2026', value: '2026' },
						{ label: '2027', value: '2027' },
						{ label: '2028', value: '2028' },
						{ label: '2029', value: '2029' },
						{ label: '2030', value: '2030' },
						{ label: '2031', value: '2031' },
						{ label: '2032', value: '2032' },
						{ label: '2033', value: '2033' },
						{ label: '2034', value: '2034' },
					]}
					onChange={(value) => onFilterChange(EFilters.PLANNER_DATE, `${value}-${month}-${day}`)}
					placeholder={t('Выберите год')}
					size="large"
					showSearch
					listHeight={OPTIONS_MAX_HEIGHT_NOTIFICATIONS}
					style={{ width: 110 }}
				/>
			</Flex>

			{type === 'day' && (
				<Flex align="center" gap={4} className={styles.dayPicker}>
					<PlannerDayCell diff={-3} />
					<PlannerDayCell diff={-2} />
					<PlannerDayCell diff={-1} />
					<PlannerDayCell diff={0} />
					<PlannerDayCell diff={1} />
					<PlannerDayCell diff={2} />
					<PlannerDayCell diff={3} />
				</Flex>
			)}

			{type === 'week' && (
				<>
					<Flex align="center" justify="center" gap={8} className={styles.arrows}>
						<Button type="text" onClick={() => onWeekChange('prev')} icon={<LeftOutlined />} />
						<Typography.Text>
							{formatDate(startWeekDate, 'dayMonth')} - {formatDate(endWeekDate, 'dayMonth')}
						</Typography.Text>
						<Button type="text" onClick={() => onWeekChange('next')} icon={<RightOutlined />} />
					</Flex>
					<Flex gap={4} className={cn(styles.dayPicker, styles.week)}>
						<PlannerDayCell diff={-(currentDayNumber - 1)} isWeek />
						<PlannerDayCell diff={-(currentDayNumber - 2)} isWeek />
						<PlannerDayCell diff={-(currentDayNumber - 3)} isWeek />
						<PlannerDayCell diff={-(currentDayNumber - 4)} isWeek />
						<PlannerDayCell diff={-(currentDayNumber - 5)} isWeek />
						<PlannerDayCell diff={-(currentDayNumber - 6)} isWeek />
						<PlannerDayCell diff={-(currentDayNumber - 7)} isWeek />
					</Flex>
				</>
			)}
		</Flex>
	);
};

export default PlannerFilters;
